import { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Alert,
  Chip,
  IconButton,
} from "@mui/material";
import Breadcrumbs from "components/common/Breadcrumbs";
import useIsMobile from "components/hooks/useIsMobile";
import {
  useListDelegationTasks,
  useUpdateDelegationTask,
} from "services/delegation";
import {
  useUpdateAdvisorService,
  useFetchAdvisorProfile,
} from "services/advisor";
import Loading from "components/common/Loading";
import { DelegationTask } from "protogen/delegations_pb";
import { Advisor } from "protogen/common_pb";
import DateDisplay from "components/common/DateDisplay";
import { Phone, Settings, Clock } from "lucide-react";
import { ReactComponent as EllipseIcon } from "../icons/Menu/Ellipse.svg";
import { getStatusLabel } from "components/delegation/DelegationCard";
import { CurrentUserContext } from "components/context/RequireAuth";
import { StatusContext } from "components/context/StatusContextProvider";
import {
  AdvisorServicesSection,
  ServiceScope,
} from "components/advisor/AdvisorServicesSettings";
import { StyledMenu } from "components/common/DropdownMenu";

type StatusFilter = "available" | "mine";

const AVAILABLE_STATUSES = ["pending", "queued"];

const formatElapsedTime = (
  createdSec: bigint,
  status: string,
): { text: string; isOverThreshold: boolean } => {
  const now = Date.now();
  const created = Number(createdSec) * 1000;
  const elapsed = now - created;

  const minutes = Math.floor(elapsed / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const isCompleted = status === "completed";
  const isCancelled = status === "cancelled";
  const isOverThreshold = !isCompleted && !isCancelled && minutes >= 30;

  let duration: string;
  if (days > 0) {
    duration = `${days}d ${hours % 24}h`;
  } else if (hours > 0) {
    duration = `${hours}h ${minutes % 60}m`;
  } else {
    duration = `${minutes}m`;
  }

  let text: string;
  if (isCompleted) {
    text = `Completed in ${duration}`;
  } else if (isCancelled) {
    text = `Queued for ${duration}`;
  } else {
    text = `${duration} open`;
  }
  return { text, isOverThreshold };
};

type OpportunityCardProps = {
  task: DelegationTask;
  onClaim: (ref: string) => void;
  onClick?: () => void;
  isClaiming: boolean;
  isHighlighted?: boolean;
  showElapsed?: boolean;
};

const OpportunityCard = ({
  task,
  onClaim,
  onClick,
  isClaiming,
  isHighlighted = false,
  showElapsed = false,
}: OpportunityCardProps) => {
  const isMobile = useIsMobile();
  const isCompleted =
    task.status === "completed" || task.status === "cancelled";
  const elapsed = isCompleted
    ? {
        text:
          task.totalTrackedMinutes > 0
            ? `Completed in ${
                task.totalTrackedMinutes >= 60
                  ? `${Math.floor(task.totalTrackedMinutes / 60)}h `
                  : ""
              }${task.totalTrackedMinutes % 60}m`
            : "No time tracked",
        isOverThreshold: false,
      }
    : task.createdSec && task.createdSec !== BigInt(0)
      ? formatElapsedTime(task.createdSec, task.status)
      : null;
  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: isHighlighted ? "2px solid #4CAF50" : "1px solid #ECECEC",
        width: "100%",
        background: "white",
        padding: "16px 24px",
        ...(onClick && {
          cursor: "pointer",
          "&:hover": {
            background: "#FAFAFA",
          },
        }),
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: isMobile ? "12px" : 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            flex: 1,
          }}
        >
          <Phone size={16} style={{ marginTop: "3px", flexShrink: 0 }} />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <Typography variant="bodyHeavy">{task.title}</Typography>
              {task.hasUnread && <EllipseIcon height={8} width={8} />}
              {task.timeEstimateMinutes > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: "text.secondary",
                  }}
                >
                  <Clock size={12} />
                  <Typography variant="bodySmall">
                    ~{task.timeEstimateMinutes}m
                  </Typography>
                </Box>
              )}
            </Box>
            {showElapsed && elapsed && (
              <Typography
                variant="body"
                sx={{
                  color: elapsed.isOverThreshold
                    ? "error.main"
                    : "text.secondary",
                }}
              >
                {elapsed.text}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            ...(isMobile ? { alignSelf: "flex-end" } : { marginLeft: "16px" }),
          }}
        >
          {task.createdSec !== undefined && task.createdSec !== BigInt(0) && (
            <DateDisplay date={new Date(Number(task.createdSec) * 1000)} />
          )}
          <Chip
            label={getStatusLabel(task.status)}
            size="small"
            variant={
              task.status === "completed"
                ? "success"
                : task.status === "in_progress" || task.status === "delegated"
                  ? "warning"
                  : task.status === "cancelled"
                    ? "error"
                    : "gray"
            }
          />
          {!task.assignee && (
            <Button
              variant="contained"
              size="small"
              sx={{
                height: "28px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClaim(task.ref);
              }}
              disabled={isClaiming}
            >
              Claim
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ({ active = false }: { active?: boolean }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);
  const [searchParams] = useSearchParams();
  const highlightRef = searchParams.get("ref");
  const {
    delegationAvailableCount,
    delegationActiveCount,
    delegationUnreadCount,
  } = useContext(StatusContext);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    highlightRef ? "available" : active ? "mine" : "available",
  );
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [advisor, setAdvisor] = useState<Advisor | null>(null);
  const { request, data, loading } = useListDelegationTasks();
  const { request: updateTask, loading: updating } = useUpdateDelegationTask(
    () => {
      refresh();
    },
  );
  const { request: fetchAdvisor } = useFetchAdvisorProfile((r) => {
    if (r.advisor) {
      setAdvisor(r.advisor);
    }
  });
  const { request: updateServiceRequest, loading: serviceLoading } =
    useUpdateAdvisorService((r) => {
      if (advisor && r.enabledServices) {
        setAdvisor(
          new Advisor({
            ...advisor,
            enabledServices: r.enabledServices,
            serviceScopes: r.serviceScopes,
          }),
        );
      }
    });

  useEffect(() => {
    fetchAdvisor({ advisorRef: currentUser.ref });
  }, []);

  useEffect(() => {
    refresh();
  }, [statusFilter]);

  const refresh = async () => {
    if (statusFilter === "mine") {
      await request({ statuses: [], assigned: true });
    } else {
      await request({ statuses: AVAILABLE_STATUSES, assigned: false });
    }
  };

  const handleClaim = async (ref: string) => {
    const success = await updateTask({
      ref,
      claim: true,
      status: "",
      commentContent: "",
      commentContentHtml: "",
      timeSpentMinutes: 0,
      unassign: false,
      attachments: [],
      timeEntriesJson: "",
      recipientType: "",
    });
    if (success) {
      navigate(`/tasks/subtasks/${encodeURIComponent(ref)}`);
    }
  };

  const handleServiceChange = async (
    serviceName: string,
    enabled: boolean,
    scope: ServiceScope,
  ) => {
    await updateServiceRequest({
      serviceName,
      enabled,
      scope,
    });
  };

  const openStatusOrder = (status: string) => {
    if (status === "in_progress" || status === "delegated") return 0;
    if (status === "queued") return 1;
    if (status === "pending") return 2;
    return 3;
  };

  const refNotFound =
    !loading &&
    highlightRef &&
    data &&
    !(data.delegationTasks || []).some((t) => t.ref === highlightRef);

  const tasks = (data?.delegationTasks || []).slice().sort((a, b) => {
    if (statusFilter === "mine") {
      const aCompleted = a.status === "completed" || a.status === "cancelled";
      const bCompleted = b.status === "completed" || b.status === "cancelled";
      if (aCompleted !== bCompleted) {
        return aCompleted ? 1 : -1;
      }
      if (!aCompleted) {
        const orderDiff = openStatusOrder(a.status) - openStatusOrder(b.status);
        if (orderDiff !== 0) return orderDiff;
        return Number(b.createdSec) - Number(a.createdSec);
      }
      return Number(b.createdSec) - Number(a.createdSec);
    }
    return 0;
  });
  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 0 64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              { name: "Home", link: "/" },
              { name: "Tasks", link: "/tasks" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="display">Subtask Opportunities</Typography>
            {advisor && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                  sx={{ color: "text.secondary" }}
                >
                  <Settings size={20} />
                </IconButton>
                <StyledMenu
                  open={Boolean(settingsAnchor)}
                  anchorEl={settingsAnchor}
                  onClose={() => setSettingsAnchor(null)}
                >
                  <Box sx={{ padding: "16px", minWidth: "280px" }}>
                    <AdvisorServicesSection
                      advisor={advisor}
                      loading={serviceLoading}
                      onServiceChange={handleServiceChange}
                      collapsible={false}
                      title="Services"
                    />
                  </Box>
                </StyledMenu>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          gap: isMobile ? "12px" : "24px",
          marginBottom: "16px",
        }}
      >
        <ButtonGroup
          style={{
            display: "flex",
            ...(isMobile && {
              width: "100%",
            }),
          }}
        >
          <Button
            sx={{ flex: "1", width: "100%", marginRight: "10px" }}
            variant={
              statusFilter === "available"
                ? "buttonGroupSelected"
                : "buttonGroup"
            }
            onClick={() => setStatusFilter("available")}
          >
            Available{" "}
            {delegationAvailableCount > 0 && `(${delegationAvailableCount})`}
          </Button>
          <Button
            sx={{ flex: "1", width: "100%", minWidth: "162px !important" }}
            variant={
              statusFilter === "mine" ? "buttonGroupSelected" : "buttonGroup"
            }
            onClick={() => setStatusFilter("mine")}
          >
            My subtasks
            {delegationActiveCount > 0 && ` (${delegationActiveCount})`}
            {!delegationActiveCount &&
              delegationUnreadCount > 0 &&
              ` (${delegationUnreadCount})`}
          </Button>
        </ButtonGroup>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "12px",
          paddingBottom: "16px",
        }}
      >
        {loading && <Loading />}
        {!loading && refNotFound && (
          <Box
            sx={{
              borderRadius: "12px",
              border: "1px solid #C8E6C9",
              background: "#F1F8F1",
              padding: "20px 24px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Chip
              label="Claimed"
              size="small"
              sx={{
                alignSelf: "flex-start",
                backgroundColor: "#C8E6C9",
                color: "#2E7D32",
                fontWeight: 600,
                fontSize: "11px",
                letterSpacing: "0.5px",
              }}
            />
            <Box
              sx={{ display: "flex", gap: "16px", alignItems: "flex-start" }}
            >
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <Typography variant="bodyHeavy">Just missed it</Typography>
                <Typography variant="body" sx={{ color: "text.secondary" }}>
                  Another advisor got here first. Sorry for the disappointment.
                  Keep an eye on the ⚡️ as new opportunities appear all the
                  time.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        {!loading && !tasks.length && !refNotFound && (
          <Alert severity="info">
            {statusFilter === "available"
              ? "No available opportunities at this time"
              : "You haven't claimed any tasks yet"}
          </Alert>
        )}
        {!loading &&
          tasks.map((task) => (
            <OpportunityCard
              key={task.ref}
              task={task}
              onClaim={handleClaim}
              onClick={
                task.assignee
                  ? () =>
                      navigate(
                        `/tasks/subtasks/${encodeURIComponent(task.ref)}`,
                      )
                  : undefined
              }
              isClaiming={updating}
              isHighlighted={highlightRef === task.ref}
              showElapsed
            />
          ))}
      </Box>
    </Box>
  );
};
